import { emojiMap } from 'smile2emoji';

const SKIN_TONE_MODIFIERS = {
  default: '',
  light: '\u{1F3FB}',
  mediumLight: '\u{1F3FC}',
  medium: '\u{1F3FD}',
  mediumDark: '\u{1F3FE}',
  dark: '\u{1F3FF}',
};

const SKIN_TONE_REGEX = /[\u{1F3FB}-\u{1F3FF}]/gu;
const EMOJI_MODIFIER_BASE_REGEX = /\p{Emoji_Modifier_Base}/gu;
const EMOJI_SHORTCODE_REGEX = /:[a-z0-9_+-]+:/gi;

/**
 * Normalizes skin tone modifiers in a text block containing emojis.
 * - Removes any existing skin tone modifiers
 * - Optionally applies the requested modifier to all modifier-capable emojis
 *
 * @param text - The original text that may contain emojis
 * @param tone - The desired skin tone to apply (or 'default' to leave unmodified)
 */
const applySkinToneToText = (text, tone = 'default') => {
  if (!text) {
    return text;
  }

  // Strip any existing skin tone modifiers first so we can apply a single, consistent tone.
  const normalized = text.replace(SKIN_TONE_REGEX, '');
  const modifier = SKIN_TONE_MODIFIERS[tone];

  if (!modifier) {
    return normalized;
  }

  // Append the desired tone to any emoji that supports modifiers.
  return normalized.replace(EMOJI_MODIFIER_BASE_REGEX, (emoji) => `${emoji}${modifier}`);
};

const replaceEmojiShortcodes = (text) => {
  if (!text) {
    return text;
  }

  return text.replace(EMOJI_SHORTCODE_REGEX, (match) => emojiMap[match.toLowerCase()] || match);
};

/**
 * Where the caret belongs after `replaceEmojiShortcodes` has run over a field's value.
 *
 * Conversion makes the rendered value shorter than the one that was typed, and the browser drops
 * the caret to the end of the field whenever those two differ. Converting the prefix up to the
 * typed caret on its own gives the answer: wherever that prefix ends is where the caret goes.
 *
 * @param {string} text The value as typed, before conversion.
 * @param {number} caret Caret offset within `text`. Omitted means the end of it.
 * @returns {number} Caret offset within the converted value.
 */
const caretAfterShortcodes = (text, caret) => replaceEmojiShortcodes((text ?? '').slice(0, caret)).length;

export {
  applySkinToneToText,
  replaceEmojiShortcodes,
  caretAfterShortcodes,
};
